import { useState } from "react";
import { useAppDispatch } from "store";
import { toast } from "react-toastify";
import { useAppTranslation } from "locale/useAppTranslation";
import { 
  useFleetServiceV1ImportFleetsMutation, 
  useFleetServiceV1ExportFleetsMutation, 
  useFleetServiceV1BulkUpdateFleetStatusMutation,
  useFleetServiceV1BulkDeleteFleetsMutation,
  fleetApi
} from "_services/modules/transport-resource/fleet";
import { showResultModal, setBulkAction } from "../store/fleetSlice";
import { useNavigate } from "react-router-dom";
import { editFleetPageRouteName } from "../../constants/routes";
import { FleetRow } from "../types";

export const useFleetActions = () => {
  const { t } = useAppTranslation("fleet" as any);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const [importFleets, { isLoading: isImportLoading }] = useFleetServiceV1ImportFleetsMutation();
  const [exportFleets, { isLoading: isExportLoading }] = useFleetServiceV1ExportFleetsMutation();
  const [bulkUpdateStatus, { isLoading: isBulkUpdating }] = useFleetServiceV1BulkUpdateFleetStatusMutation();
  const [bulkDeleteFleets, { isLoading: isBulkDeleting }] = useFleetServiceV1BulkDeleteFleetsMutation();

  const handleStatusUpdate = async (ids: string[], plateNumbers: string[], status: string) => {
    if (status === "Delete") {
      try {
        await bulkDeleteFleets({
          transportResourceBulkDeleteFleetsReq: {
            ids: ids,
          },
        }).unwrap();

        dispatch(setBulkAction({ status: null, showModal: false }));
        dispatch(showResultModal({
          type: "success",
          title: t("bulk-delete-success-title" as any),
          message: t("bulk-delete-success-message" as any, { 
            name: ids.length === 1 ? plateNumbers[0] : `${ids.length} ${t("unit-fleets" as any)}` 
          }),
        }));
        dispatch(fleetApi.util.invalidateTags(["Fleet"]));
      } catch (error) {
        toast.error(t("toast-delete-failed" as any));
      }
      return;
    }

    try {
      await bulkUpdateStatus({
        transportResourceBulkUpdateFleetStatusReq: {
          ids: ids,
          status: status,
        },
      }).unwrap();

      dispatch(setBulkAction({ status: null, showModal: false }));
      
      dispatch(showResultModal({
        type: "success",
        title: t("toast-bulk-update-success" as any),
        message: t("bulk-activate-success-message" as any, { 
          count: ids.length, 
          unit: ids.length === 1 ? t("unit-fleet" as any) : t("unit-fleets" as any) 
        }),
      }));
      
      dispatch(fleetApi.util.invalidateTags(["Fleet"]));
    } catch (error) {
      dispatch(showResultModal({
        type: "error",
        title: t("bulk-update-failed-title" as any),
        message: t("toast-bulk-update-failed" as any),
      }));
    }
  };

  const handleConfirmDelete = async (deleteRow: FleetRow | null, setShowDeleteConfirmModal: (s: boolean) => void) => {
    if (!deleteRow) return;
    try {
      await bulkDeleteFleets({
        transportResourceBulkDeleteFleetsReq: {
          ids: [deleteRow.id],
        },
      }).unwrap();

      dispatch(showResultModal({
        type: "success",
        title: t("bulk-delete-success-title" as any),
        message: t("bulk-delete-success-message" as any, { name: deleteRow.plateNumber }),
      }));
      setShowDeleteConfirmModal(false);
      dispatch(fleetApi.util.invalidateTags(["Fleet"]));
    } catch (error) {
      toast.error(t("toast-delete-failed" as any));
    }
  };

  const handleExport = async (
    format: string,
    type: string,
    setIsExportOpen: (s: boolean) => void,
    filters?: any,
    selectedKeys?: (string | number)[]
  ) => {
    try {
      const ids = type === "selected" ? (selectedKeys as string[]) : undefined;
      const blob = await exportFleets({
        transportResourceExportFleetsRequest: {
          format,
          ids,
          scope: type === "selected" ? "selected" : "all",
          filters: type === "selected" ? undefined : filters,
        },
      }).unwrap();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Fleet_Export_${new Date().getTime()}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);

      setIsExportOpen(false);
      toast.success(t("toast-export-success" as any));
    } catch (error) {
      toast.error(t("toast-export-failed" as any));
    }
  };

  const handleEditRequest = (id: string) => {
    navigate(editFleetPageRouteName(id));
  };

  return {
    handleStatusUpdate,
    handleConfirmDelete,
    handleExport,
    handleEditRequest,
    isImportLoading,
    isExportLoading,
    isBulkUpdating: isBulkUpdating || isBulkDeleting,
    importFleets,
  };
};
